import React, { useState, useEffect, useRef } from "react";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./UserCreatedFormsList.css";
import usePageTitle from "../../usePageTitle";
import UserCreatedFormsListHeaderComponent from "./UserCreatedFormsListHeaderComponent";
import UserCreatedFormsListDatatableComponent from "./UserCreatedFormsListDatatableComponent";
import { FilterMatchMode } from "primereact/api";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import { fetchUserCreatedForms, updateStatus } from "./api";

const UserCreatedFormsList = () => {
  usePageTitle("My Created Forms List - SkeyeForm™");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(24);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [editingUsageRow, setEditingUsageRow] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const forms = await fetchUserCreatedForms();
        if (Array.isArray(forms)) {
          // Only show active forms (not soft deleted)
          const activeForms = forms.filter((form) => !form.softDelete);
          setTotalRecords(activeForms.length);
          setData(activeForms);
          setFilteredData(activeForms.slice(first, first + rows));
        } else {
          console.error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error fetching user forms:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [first, rows]);

  useEffect(() => {
    if (Array.isArray(data)) {
      let filtered = data.filter((project) => {
        const formattedDate = project.createdAt
          ? new Date(project.createdAt).toLocaleString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
          : "";
        return (
          (project.title &&
            project.title
              .toLowerCase()
              .includes(globalFilterValue.toLowerCase())) ||
          (formattedDate && formattedDate.includes(globalFilterValue)) ||
          (project.status &&
            project.status.toString().includes(globalFilterValue))
        );
      });

      if (sortField) {
        filtered = filtered.sort((a, b) => {
          const valueA = a[sortField];
          const valueB = b[sortField];
          let result = 0;

          if (valueA < valueB) result = -1;
          else if (valueA > valueB) result = 1;

          return sortOrder * result;
        });
      }
      setFilteredData(filtered.slice(first, first + rows));
      setTotalRecords(filtered.length);
    }
  }, [globalFilterValue, first, rows, data, sortField, sortOrder]);

  const handleFormsUploaded = (uploadedForms) => {
    if (Array.isArray(uploadedForms)) {
      // Update the main data state
      setData((prevData) => {
        const updatedData = [...prevData];

        // Process each uploaded form
        uploadedForms.forEach((newForm) => {
          // Check if form already exists
          const existingIndex = updatedData.findIndex(
            (form) => form.id === newForm.id
          );

          if (existingIndex >= 0) {
            // Replace existing form
            updatedData[existingIndex] = newForm;
          } else {
            // Add new form if it's not deleted
            if (!newForm.softDelete) {
              updatedData.push(newForm);
            }
          }
        });

        // Update total records count
        setTotalRecords(updatedData.length);
        return updatedData;
      });

      // Force immediate refresh of filtered data
      setTimeout(() => {
        // This will trigger the useEffect that updates filteredData
        if (globalFilterValue) {
          const tempValue = globalFilterValue;
          setGlobalFilterValue("");
          setTimeout(() => setGlobalFilterValue(tempValue), 10);
        } else {
          // If no filter, just refresh the data
          const currentFirst = first;
          setFirst(0);
          setTimeout(() => setFirst(currentFirst), 10);
        }
      }, 50);
    }
  };

  const handleFormDeleted = (deletedFormId, deleteType) => {
    if (deleteType === "hard") {
      const updatedData = data.filter((form) => form.id !== deletedFormId);
      setData(updatedData);

      const updatedFilteredData = filteredData.filter(
        (form) => form.id !== deletedFormId
      );
      setFilteredData(updatedFilteredData);
      setTotalRecords(updatedData.length);
    } else {
      const updatedData = data.filter((form) => form.id !== deletedFormId);
      setData(updatedData);

      const updatedFilteredData = filteredData.filter(
        (form) => form.id !== deletedFormId
      );
      setFilteredData(updatedFilteredData);
      setTotalRecords(updatedData.length);
    }
  };

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFirst(0);
  };

  const clearFilter = () => {
    setGlobalFilterValue("");
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  const onSort = (e) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder);
  };

  const handleStatusChange = async (formId, currentStatus, formTitle) => {
    try {
      const newStatus = currentStatus === "up" ? "down" : "up";
      const updatedForm = await updateStatus(formId, newStatus);
      setData((prevData) =>
        prevData.map((form) =>
          form.id === formId ? { ...form, status: updatedForm.status } : form
        )
      );
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: `Form "${formTitle}" status updated to "${updatedForm.status}"`,
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update form status",
        life: 3000,
      });
    }
  };

  const header = (
    <UserCreatedFormsListHeaderComponent
      globalFilterValue={globalFilterValue}
      onGlobalFilterChange={onGlobalFilterChange}
      clearFilter={clearFilter}
      onFormsUploaded={handleFormsUploaded}
    />
  );

  return (
    <div className="p-grid p-dir-col table-page">
      <Toast ref={toast} />
      <div className="p-col title">
        <h1>My Forms List</h1>
        <div className="title-bar"></div>
      </div>
      {loading ? (
        <div className="loader-container">
          <ProgressSpinner
            height={40}
            width={40}
            color="#D97A07"
            wrapperStyle={{ display: "inline-block", verticalAlign: "middle" }}
            wrapperClass=""
            visible={true}
            ariaLabel="oval-loading"
            secondaryColor="#D97A07"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
          <span
            style={{
              marginLeft: "10px",
              verticalAlign: "middle",
              fontSize: "20px",
              fontFamily: "Poppins",
            }}
          >
            Fetching Data (This Might Take a Minute)...
          </span>
        </div>
      ) : (
        <UserCreatedFormsListDatatableComponent
          data={filteredData}
          filters={filters}
          setFilters={setFilters}
          header={header}
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          handleStatusChange={handleStatusChange}
          emptyMessage="No forms assigned to you"
          onFormDeleted={handleFormDeleted}
          onSort={onSort}
          sortField={sortField}
          sortOrder={sortOrder}
          editingUsageRow={editingUsageRow}
          onEditingUsageRowChange={setEditingUsageRow}
        />
      )}
    </div>
  );
};

export default UserCreatedFormsList;
